import { useMemo } from 'react'
import { dataCatalog } from '../../../../api/coreData'

const SEARCH_PARAMS = {
  filters: { isAiEnabled: true },
  includes: { schema: true },
  take: 500
}

/**
 * Hook to fetch and lookup catalog datasources by type and name
 * Used to enrich tool call data sources with catalog metadata (display name, icon, etc.)
 *
 * @returns {{ getCatalogDatasource: Function, isLoading: boolean }}
 */
export function useDataSourceCatalogLookup () {
  const { data, isLoading } = dataCatalog.useSearchCatalogDataSources(SEARCH_PARAMS)

  const lookupMaps = useMemo(() => {
    if (!data?.data) {
      return { viewMap: new Map(), apiMap: new Map() }
    }

    const viewMap = new Map()
    const apiMap = new Map()

    for (const ds of data.data) {
      if (ds.type === 'table') {
        // For views/tables, key is "schema.code"
        const schemaCode = ds.schema?.code || ''
        const key = `${schemaCode}.${ds.code}`.toLowerCase()
        viewMap.set(key, ds)
      } else if (ds.type === 'api') {
        // For APIs, key is just the code (pipeline code)
        apiMap.set(ds.code.toLowerCase(), ds)
      }
    }

    return { viewMap, apiMap }
  }, [data])

  /**
   * Get catalog datasource by type and name from tool call summary
   * @param {'view' | 'api'} type - The data source type
   * @param {string} name - For views: "schema.code", for APIs: "pipelineCode"
   * @returns {object | null} The catalog datasource or null if not found
   */
  const getCatalogDatasource = useMemo(() => {
    return (type, name) => {
      if (!name) return null

      const normalizedName = name.toLowerCase()

      if (type === 'view') {
        return lookupMaps.viewMap.get(normalizedName) || null
      } else if (type === 'api') {
        return lookupMaps.apiMap.get(normalizedName) || null
      }

      return null
    }
  }, [lookupMaps])

  return {
    getCatalogDatasource,
    isLoading
  }
}
